import React from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { FixedSizeList as List } from 'react-window';

import optimizeImage from '../../../shared/utils/imageOptimizer';
import Text from '../../../shared/components/Text/Text';

// Add a styled wrapper for the List component with custom scrollbar styles
const StyledList = styled(List)`
  /* Hide the default scrollbar track */
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  /* Style the scrollbar thumb */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  /* Style the scrollbar thumb */
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.scrollbarThumb};
    border-radius: 3px;
  }
  
  /* For Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.scrollbarThumb} transparent;
`;

const AthleteContainer = styled('div')`
  display: flex;
  padding: 10px;
  cursor: ${({ isFake }) => (isFake ? 'default' : 'pointer')};
  &:hover {
    background-color: ${({ isFake, theme }) => (isFake ? 'transparent' : theme.colors.surfaceSecondary)};
  }
  img {
    height: 30px;
    width: 30px;
  }
  h3 {
    color: ${({ theme }) => theme.colors.textStrong};
  }
  svg {
    visibility: hidden;
  }
  @media screen and (orientation: portrait) {
    padding: 10px 20px!important;
  }
`;

const ListAthleteAvatar = styled('div')`
  img {
    height: ${(props) => (
    props.size ? `${props.size}px` : '25px')};
    width: ${(props) => (
    props.size ? `${props.size}px` : '25px')};
    border-radius: 50%;
    margin-right: 10px;
  }
  display: flex;
  align-items: center;
`;

const AthleteNameContainer = styled('div')`
  display: flex;
  align-items: center;
`;

const ListAthleteName = styled('h3')`
  font-family: 'Nunito Sans';
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 0px;
  align-items: center;
  font-weight: 600;
  font-size: 18px;
  @media screen and (orientation: portrait) {
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: ${(props) => (props.small ? '85px;' : 'none')}
  }
`;

/**
 * @param {Object} data an object that's passed in from the 'itemData' prop in the list component
 * seen below
 * @param {Integer} index the index of the current data object in the passed array
 * @param {Object} style necessary object that styles each individual container in accordance with
 * the requirements of the list
 * @returns a formatted list of dynamically rendered athlete rows
 */
const AthleteRenderer = ({
  data, index, style,
}) => {
  const {
    list, onOptionClicked,
  } = data;
  const athlete = list[index];
  const isFakeAthlete = athlete.fake;
  const image = optimizeImage(
    athlete.pic,
    {
      resize: {
        width: 70,
        height: 70,
        fit: 'cover',
      },
    },
  );

  return (
    <AthleteContainer
      onClick={isFakeAthlete ? undefined : () => {
        onOptionClicked(athlete);
      }}
      id={athlete.userId}
      isFake={isFakeAthlete}
      style={style}
    >
      {athlete.pic ? (
        <ListAthleteAvatar>
          <img src={image} alt='' />
        </ListAthleteAvatar>
      ) : null}
      <AthleteNameContainer>
        <ListAthleteName>
          <Text>
            {`${athlete.first} ${athlete.last}`}
          </Text>
        </ListAthleteName>
      </AthleteNameContainer>
    </AthleteContainer>
  );
};

/**
 *
 * @param {Array} list the list of athletes to be passed into a generated windowed list
 * @param {Function} reduxFunction the function to be called when the user clicks on
 * an athlete row in the windowed list
 * @param {Object} visibleContainerItem the item that is generated in the selector container
 * i.e. the currently selected item
 * @returns
 */
const CalendarSelectAthleteList = ({
  reduxFunction,
  calendarSelectAthletes,
  isUserSearching,
  searchedAthleteList,
}) => {
  const onOptionClicked = (clickedOption) => {
    reduxFunction(clickedOption);
  };

  const listMaker = () => {
    if (isUserSearching) {
      if (searchedAthleteList.length) {
        return searchedAthleteList;
      }
    }
    return calendarSelectAthletes;
  };

  return (
    <StyledList
      height={300}
      itemData={{
        list: listMaker(),
        onOptionClicked,
        isUserSearching,
      }}
      itemCount={!isUserSearching ? calendarSelectAthletes.length : searchedAthleteList.length}
      itemSize={55}
      width='100%'
      className='fixed-list'
    >
      {AthleteRenderer}
    </StyledList>
  );
};

AthleteRenderer.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  index: PropTypes.number.isRequired,
  style: PropTypes.instanceOf(Object).isRequired,
};

CalendarSelectAthleteList.propTypes = {
  reduxFunction: PropTypes.func.isRequired,
  calendarSelectAthletes: PropTypes.instanceOf(Array).isRequired,
  isUserSearching: PropTypes.bool.isRequired,
  searchedAthleteList: PropTypes.instanceOf(Array).isRequired,
};

export default CalendarSelectAthleteList;
